import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from 'axios';
import { decorate, injectable } from 'inversify';
import 'reflect-metadata';
import { merge, omit } from 'lodash';
import { CancelError } from 'errors';

type RequestData = Record<string, unknown>;
type Headers = Record<string, unknown>;
type RequestConfig = AxiosRequestConfig & { headers?: Headers };
type HttpConfig = RequestConfig & { axiosInstance?: AxiosInstance };

const DEFAULT_CONFIG: AxiosRequestConfig = {
  timeout: 60000,
};

const DEFAULT_HEADERS: Headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

class HttpService {
  axiosInstance!: AxiosInstance;

  headers: Headers = {};

  setConfig(config: HttpConfig): AxiosInstance {
    this.headers = merge({}, DEFAULT_HEADERS, config.headers || {});

    if (config.axiosInstance) {
      this.axiosInstance = config.axiosInstance;
    } else {
      const combinedConfig = merge({}, DEFAULT_CONFIG, omit(config, ['headers']));
      this.axiosInstance = axios.create(combinedConfig);
    }

    return this.axiosInstance;
  }

  setHeader(name: string, value: unknown): AxiosInstance {
    this.headers[name] = value;

    return this.axiosInstance;
  }

  setHeaders(values: Headers): AxiosInstance {
    Object.entries(values).forEach(([name, value]) => this.setHeader(name, value));

    return this.axiosInstance;
  }

  deleteHeader(name: string): AxiosInstance {
    delete this.headers[name];

    return this.axiosInstance;
  }

  setBaseURL(newURL: string): AxiosInstance {
    this.axiosInstance.defaults.baseURL = newURL;

    return this.axiosInstance;
  }

  getBaseURL(): string | undefined {
    return this.axiosInstance.defaults.baseURL;
  }

  getRequestAbortionPieces(): {
    abort: CancelTokenSource;
    connectionTimeout: ReturnType<typeof setTimeout>;
  } {
    const { timeout } = this.axiosInstance.defaults;
    const abort = axios.CancelToken.source();
    const connectionTimeout = setTimeout(
      () => abort.cancel(`Connection timeout of ${timeout}ms.`),
      timeout,
    );

    return { abort, connectionTimeout };
  }

  async doRequest(
    axiosRequestConfig: RequestConfig,
  ): Promise<AxiosResponse<unknown>> {
    const { abort, connectionTimeout } = this.getRequestAbortionPieces();
    const requestConfig: AxiosRequestConfig = {
      ...axiosRequestConfig,
      cancelToken: abort.token,
      headers: {
        ...this.headers,
        ...axiosRequestConfig.headers,
      } as AxiosRequestConfig['headers'],
    };

    try {
      const response = await this.axiosInstance.request(requestConfig);
      clearTimeout(connectionTimeout);
      return response;
    } catch (e) {
      if (axios.isCancel(e)) {
        const cancelError = new CancelError();
        cancelError.message = e instanceof Error ? e.message : String(e);
        throw cancelError;
      }

      throw e;
    }
  }

  doRequestWithoutBody(
    method: string,
    url: string,
    params: RequestData = {},
    axiosConfig: RequestConfig = {},
  ): Promise<AxiosResponse<unknown>> {
    return this.doRequest(merge({}, { url, params, method }, axiosConfig));
  }

  doRequestWithBody(
    method: string,
    url: string,
    data: RequestData | null = null,
    axiosConfig: RequestConfig = {},
  ): Promise<AxiosResponse<unknown>> {
    return this.doRequest(merge({}, { url, method, data }, axiosConfig));
  }

  get = (
    url: string,
    params: RequestData = {},
    axiosConfig: RequestConfig = {},
  ): Promise<AxiosResponse<unknown>> =>
    this.doRequestWithoutBody('get', url, params, axiosConfig);

  delete = (
    url: string,
    params: RequestData = {},
    axiosConfig: RequestConfig = {},
  ): Promise<AxiosResponse<unknown>> =>
    this.doRequestWithoutBody('delete', url, params, axiosConfig);

  head = (
    url: string,
    params: RequestData = {},
    axiosConfig: RequestConfig = {},
  ): Promise<AxiosResponse<unknown>> =>
    this.doRequestWithoutBody('head', url, params, axiosConfig);

  post = (
    url: string,
    data: RequestData | null = null,
    axiosConfig: RequestConfig = {},
  ): Promise<AxiosResponse<unknown>> =>
    this.doRequestWithBody('post', url, data, axiosConfig);

  put = (
    url: string,
    data: RequestData | null = null,
    axiosConfig: RequestConfig = {},
  ): Promise<AxiosResponse<unknown>> =>
    this.doRequestWithBody('put', url, data, axiosConfig);

  patch = (
    url: string,
    data: RequestData | null = null,
    axiosConfig: RequestConfig = {},
  ): Promise<AxiosResponse<unknown>> =>
    this.doRequestWithBody('patch', url, data, axiosConfig);

  link = (
    url: string,
    params: RequestData = {},
    axiosConfig: RequestConfig = {},
  ): Promise<AxiosResponse<unknown>> =>
    this.doRequestWithoutBody('link', url, params, axiosConfig);

  unlink = (
    url: string,
    params: RequestData = {},
    axiosConfig: RequestConfig = {},
  ): Promise<AxiosResponse<unknown>> =>
    this.doRequestWithoutBody('unlink', url, params, axiosConfig);
}

(decorate as (decorator: ClassDecorator, target: unknown) => void)(
  injectable() as ClassDecorator,
  HttpService,
);

export default HttpService;
